/**
 * BALANCE TABLE - every tunable number in the game lives here.
 *
 * Nothing in the systems hard-codes a magic value. The headless balance
 * harness reads these, runs hundreds of games, and reports whether the
 * $100 -> $10,000 path is legitimately reachable and free of dominant or
 * degenerate strategies. Tuning the game means editing this file.
 *
 * All money values are in CENTS.
 */
import { dollars } from '../core/money.js';

export const BALANCE = {
  /** Player starts here. The brief's premise, expressed literally. */
  startingCash: dollars(100),
  startingBank: dollars(0),

  /** Target used for the progression gate. */
  netWorthGoal: dollars(10_000),

  time: {
    /** In-game minutes advanced per real second at 1x speed. */
    minutesPerRealSecond: 1,
    minutesPerDay: 1440,
    /** Player is forced to sleep past this hour; sleeping restores energy. */
    wakeHour: 6,
    sleepHourLatest: 26, // 02:00 next day
    availableSpeeds: [0, 1, 2, 4] as const,
  },

  needs: {
    /** Points per in-game hour. 100 -> 0 in ~28h awake, so eating is a daily act. */
    hungerDecayPerHour: 3.6,
    energyDecayPerHour: 3.2,
    /** Working is tiring on top of the baseline. */
    energyDecayPerHourWorking: 3.4,
    /** Per hour asleep. */
    energyRecoveryPerHour: 12.5,
    /** Below this the player is slowed and job quality suffers. */
    lowThreshold: 30,
    /** Below this they are barely functional. */
    criticalThreshold: 10,
    /** Multiplier applied to work speed when a need is low. */
    lowPenalty: 0.62,
    /** Multiplier when a need is critical. Running on empty must not be free. */
    criticalPenalty: 0.35,
    startingHunger: 80,
    startingEnergy: 90,
  },

  expenses: {
    /** Rent is charged weekly on the rent day. Missing it accrues debt + reputation loss. */
    rentDayOfWeek: 0, // Monday
    phoneWeekly: dollars(12),
    /** Charged per day the player owns a vehicle. */
    vehicleUpkeepDaily: dollars(3),
    /** Business equipment maintenance, per item per week. */
    equipmentUpkeepWeekly: dollars(2),
    /** Bus fare, an alternative to walking. */
    busFare: dollars(2.5),
    /** Missing rent converts into debt at this interest per week. */
    lateFeeRate: 0.08,
  },

  debt: {
    /** Small emergency loan the bank will extend. */
    maxLoan: dollars(1500),
    /** Weekly interest on outstanding debt. */
    weeklyInterest: 0.045,
    /** Minimum weekly repayment, as a fraction of the principal. */
    minWeeklyRepaymentRate: 0.1,
    /** Reputation floor required before the bank will lend at all. */
    minReputationForLoan: 0,
  },

  jobs: {
    courier: {
      /**
       * Pay range per delivery. Deliberately below a shift's hourly rate once
       * travel is counted: gigs are the flexible gap-filler and the safety net
       * that means the player can never be hard-stuck, not the optimal play.
       */
      minPay: dollars(5),
      maxPay: dollars(11),
      /** In-game minutes of actual work, excluding travel. */
      workMinutes: 12,
      /** How many are available at once. */
      concurrentOffers: 3,
      /**
       * One new offer appears this often. This is the cap on gig income: at
       * ~14 waking hours that is about a dozen gigs a day, deliberately worth
       * less than a day of yard work.
       */
      refreshMinutes: 70,
      availableFromHour: 7,
      availableToHour: 21,
    },
    shift: {
      /**
       * A fixed block: show up, work it, get paid. Four hours at this rate is
       * $60 - exactly the price of the starter mower, so the first day of the
       * game has an obvious, legible goal.
       */
      hourlyPay: dollars(15),
      hours: 4,
      /** Latest hour you can still start a shift. */
      startFromHour: 7,
      startToHour: 15,
      /** One per day. */
      perDay: 1,
      /** Energy is heavily drained by a full shift. */
      energyCost: 22,
    },
  },

  yard: {
    /** Base pay by lot size, before reputation and quality modifiers. */
    payBySize: {
      small: dollars(34),
      medium: dollars(51),
      large: dollars(77),
    },
    /** Base in-game minutes of work with the entry-level mower only. */
    minutesBySize: {
      small: 58,
      medium: 84,
      large: 120,
    },
    /**
     * What a district is worth, and what it takes to be called there.
     *
     * This is the progression fantasy expressed as two numbers per tier. You
     * can see Hillcrest from day one and you cannot work there, because
     * nobody up there has heard of you; the reputation gate is the whole
     * point of the street being visible. The pay multiplier has to cover the
     * extra travel and then some, or the unlock is a punishment - a Hillcrest
     * round trip is roughly 12 minutes further on foot than a Maple Grove
     * one, which the multiplier is set to more than repay.
     *
     * Sized against the sweep rather than by feel. Flat multipliers of 1 put
     * the median goal day at 15; 1.3/1.75 pulled it to 13, which made the
     * richer districts an accelerant rather than a choice. These land it at
     * 14 - the unlock is worth taking without being the only thing worth
     * doing.
     */
    tierMinReputation: {
      starter: 0,
      middle: 10,
      wealthy: 30,
    },
    tierPayMultiplier: {
      starter: 1,
      middle: 1.22,
      wealthy: 1.6,
    },
    /**
     * What you need to be able to get there with a mower.
     *
     * The second half of what makes a vehicle matter, and the half ADR-0011
     * left open. Distance alone only makes the far districts slower; this
     * makes them unreachable until you have wheels, so buying one is a
     * business decision rather than a convenience.
     *
     * 'none' - walkable, and the world test asserts every starter lot is.
     * 'any'  - a bicycle will do; you can strap a mower to one.
     * 'car'  - you are not taking a mower up Hillcrest on a bicycle.
     *
     * Layered on top of the reputation gates rather than replacing them, so
     * the two unlocks land at different moments: reputation 10 opens the
     * middle districts around the time a $95 bicycle is affordable, and
     * reputation 30 opens Hillcrest around the time a $900 hatchback is a
     * stretch worth making.
     */
    tierMinVehicle: {
      starter: 'none',
      middle: 'any',
      wealthy: 'car',
    } as Record<'starter' | 'middle' | 'wealthy', 'none' | 'any' | 'car'>,
    /** Pay bonus applied per point of reputation, as a fraction. */
    payPerReputation: 0.006,
    /** Cap on the reputation pay multiplier. */
    maxReputationPayBonus: 0.45,
    /** Quality below this annoys the customer. */
    qualityGoodThreshold: 0.7,
    /** Tip as a fraction of pay when quality is excellent. */
    tipRate: 0.12,
    tipQualityThreshold: 0.88,
  },

  leads: {
    /**
     * New leads generated per day, scaled by reputation.
     *
     * The ceiling matters more than it looks. An early balance sweep found that
     * hiring made the player POORER, and the cause was here: supply capped out
     * at roughly what one well-equipped person could do in a day, so a crew had
     * nothing to work on and was pure wage cost. Demand must outgrow a single
     * pair of hands, or delegation can never pay - which would have made the
     * whole business half of the game pointless.
     */
    baseLeadsPerDay: 2.0,
    /**
     * Reputation is what turns demand on. The slope is set so that:
     *   rep 0   -> ~2 leads/day  : scarce; the player fills the day with gigs
     *   rep 25  -> ~7 leads/day  : matched to a basic kit
     *   rep 50  -> ~11 leads/day : a basic kit can no longer keep up, so
     *                              faster equipment starts converting into money
     *   rep 100 -> ~20 leads/day : beyond any one person, so hiring is the
     *                              only way to capture it
     * Without that last band, equipment speed and employees are both worthless:
     * a sweep with a lower ceiling showed a player with one $60 mower matching
     * a fully equipped one, because both simply cleared the day's queue.
     */
    leadsPerReputationPoint: 0.13,
    maxLeadsPerDay: 15,
    /** A lead disappears if not accepted within this many in-game hours. */
    expiryHours: 30,
    /** Max simultaneously open leads. */
    maxOpen: 30,
    /**
     * When a household stops being a stranger.
     *
     * Repeat business is the thing that makes a neighbourhood feel like one
     * rather than a queue of anonymous jobs, so the same faces have to come
     * back - and the player has to be able to see that they have. Thresholds
     * are jobs completed; the rung also needs the satisfaction beside it, so
     * doing six bad jobs for the same house does not make them loyal.
     */
    regularAfterJobs: 3,
    regularMinSatisfaction: 0.6,
    loyalAfterJobs: 6,
    loyalMinSatisfaction: 0.75,
    /**
     * How much a standing relationship pulls the next lead toward the same
     * household. This moves WHO offers work, not how much it pays, so it
     * cannot inflate the economy - which is the mistake the trait system
     * made twice before it was mean-centred.
     */
    loyaltyLeadWeight: 0.5,
    /** Extra tip rate from a loyal customer, as a fraction of the base rate. */
    loyaltyTipBonus: 0.25,
    /** Chance a satisfied customer offers a recurring contract. */
    contractOfferChance: 0.3,
    contractMinReputation: 20,
    /** A recurring contract schedules work every N days. */
    contractIntervalDays: 7,
    /** Contract pay multiplier vs a one-off job (slight discount for reliability). */
    contractPayMultiplier: 0.92,
  },

  reputation: {
    start: 0,
    max: 100,
    /**
     * Change per completed job, scaled by quality, and then by how far the
     * player already is up the scale - see `ReputationSystem.applyJobResult`.
     * Flat gains let reputation max out inside a fortnight, which pulled the
     * entire demand curve forward and had the balance sweep reaching the
     * $10,000 goal on day 11, before any of the ladder could be felt.
     */
    perGoodJob: 3.2,
    perExcellentJob: 4.8,
    perPoorJob: -4.5,
    /**
     * Gains are multiplied by `(1 - rep/max) * this`. Early jobs move the needle
     * hard, which is exactly when the player needs to see it working; the last
     * stretch to a spotless reputation is a long tail.
     */
    diminishingFactor: 1.45,
    /** Declining or letting a lead expire has a small cost. */
    perExpiredLead: -0.8,
    perDeclinedLead: -0.15,
    perMissedContract: -6,
    /** Passive decay per week, so reputation must be maintained. */
    weeklyDecay: 0.6,
  },

  employees: {
    /** Hourly wage range for a first hire. */
    minWage: dollars(15),
    maxWage: dollars(20),
    /** Hours an employee works per working day. */
    hoursPerDay: 7,
    /** Days per week an employee works. */
    daysPerWeek: 5,
    /** Wages are paid weekly. */
    payDayOfWeek: 5, // Saturday
    /** An employee needs their own equipment set before they can work. */
    requiresEquipmentSet: true,
    /** Skill 0..1; scales speed and quality. Grows slowly with experience. */
    startingSkillMin: 0.45,
    startingSkillMax: 0.75,
    skillGrowthPerJob: 0.004,
    maxSkill: 1.0,
    /** Employees work slower than an equally-equipped owner. */
    speedFactor: 0.85,
    /** Quality penalty vs the owner, before skill. */
    qualityFactor: 0.9,
    /** Chance per day an employee is unavailable. */
    absenceChance: 0.04,
    /** Hiring cost (advertising, onboarding). */
    hiringFee: dollars(75),
    maxEmployees: 4,
  },

  business: {
    /** Registering the business unlocks contracts and employees. */
    registrationFee: dollars(120),
    /** Reputation required before the player may register. */
    registrationMinReputation: 8,
    /** Business licence upkeep, weekly. */
    licenceWeekly: dollars(6),
  },

  /**
   * Tools wear out, and the ladder is about more than the next purchase.
   *
   * Sized against the one rule ADR-0008 set for the fuel system and this
   * project has stuck to since: the failure mode to avoid is a chore. So the
   * wear is slow enough that servicing is a decision made a handful of times
   * in a run rather than a meter to babysit, and - this is the important part
   * - **condition never stops a tool working**. A worn mower is slower and
   * finishes rougher; it never refuses to start. A player locked out of their
   * only income by a maintenance bill they could not afford would be the
   * worst thing this system could do, and it is structurally impossible.
   */
  equipment: {
    /**
     * Condition lost per in-game hour of work.
     *
     * At roughly 90 minutes a job this is about 40 jobs from new to the
     * warning threshold - three or four in-game weeks of steady work, so a
     * service is an occasional errand rather than a routine.
     */
    wearPerHour: 0.011,
    /** Below this, the phone says so. */
    warnBelow: 0.45,
    /**
     * How far a worn tool falls off.
     *
     * At condition 0 a tool keeps its floor share of its speed and quality
     * bonus - it is a bad version of itself, not a brick. A brand-new push
     * mower is `speed` 1.0 either way, so a beginner with nothing else
     * notices nothing, which is deliberate: the first hour of the game should
     * not be about maintenance.
     */
    wornSpeedFloor: 0.55,
    wornQualityFloor: 0.6,
    /**
     * Cost of a full service, as a fraction of the tool's price, for a tool
     * worn all the way down. Servicing early costs proportionally less.
     */
    serviceCostFraction: 0.22,
    /** In-game minutes a service takes. */
    serviceMinutes: 30,
  },

  travel: {
    /** Metres per in-game minute, on foot. */
    walkSpeed: 22,
    runSpeed: 40,
    /** Multiplier on effective travel time when a vehicle is owned. */
    bicycleFactor: 0.55,
    vehicleFactor: 0.3,
  },

  /**
   * Running a vehicle.
   *
   * The point of these numbers is that driving is a *decision*, not a free
   * upgrade (Priority 7, and the tension ADR-0011 records). A tank costs
   * roughly one small lawn, and crosses the neighbourhood many times over -
   * so fuel is a background cost that occasionally bites, rather than a meter
   * to babysit. If it ever feels like the latter, raise the capacity rather
   * than lowering the price: the failure mode to avoid is a chore.
   */
  vehicle: {
    /** Litres. */
    fuelCapacity: 38,
    /** Litres consumed per kilometre driven. */
    litresPerKm: 0.11,
    /** Price per litre at the depot pump. */
    fuelPricePerLitre: dollars(1.62),
    /** Below this fraction the phone warns, once per refill. */
    lowFuelWarning: 0.18,
  },

  /** Milestones drive the goals list on the phone and the progression gate. */
  milestones: [
    { id: 'first_dollar', label: 'Earn your first dollar', netWorth: null, cashEarned: dollars(1) },
    { id: 'first_tool', label: 'Buy your first piece of equipment', netWorth: null, equipmentCount: 1 },
    { id: 'nw_500', label: 'Reach $500 net worth', netWorth: dollars(500) },
    { id: 'nw_1000', label: 'Reach $1,000 net worth', netWorth: dollars(1000) },
    { id: 'registered', label: 'Register your business', businessRegistered: true },
    { id: 'nw_2500', label: 'Reach $2,500 net worth', netWorth: dollars(2500) },
    { id: 'first_hire', label: 'Hire your first employee', employeeCount: 1 },
    { id: 'nw_5000', label: 'Reach $5,000 net worth', netWorth: dollars(5000) },
    { id: 'nw_10000', label: 'Reach $10,000 net worth', netWorth: dollars(10_000) },
  ],
} as const;

export type Balance = typeof BALANCE;
